import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ClipboardList, AlertTriangle } from 'lucide-react';
import { format, subDays, startOfDay, eachDayOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface EventsChartProps {
  executions: {
    executed_at: string;
    is_alert: boolean;
  }[];
}

export function EventsChart({ executions }: EventsChartProps) {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const endDate = new Date();
    const startDate = subDays(endDate, 29);

    // Create array of all days in the range
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Count events per day
    const countsMap = new Map<string, { normal: number; alerts: number }>();

    days.forEach(day => {
      const key = format(day, 'yyyy-MM-dd');
      countsMap.set(key, { normal: 0, alerts: 0 });
    });

    executions.forEach(exec => {
      const day = format(startOfDay(new Date(exec.executed_at)), 'yyyy-MM-dd');
      const count = countsMap.get(day);
      if (count) {
        if (exec.is_alert) {
          count.alerts += 1;
        } else {
          count.normal += 1;
        }
      }
    });

    return days.map(day => {
      const key = format(day, 'yyyy-MM-dd');
      const counts = countsMap.get(key) || { normal: 0, alerts: 0 };
      return {
        date: format(day, 'dd/MM', { locale: ptBR }),
        fullDate: format(day, "d 'de' MMM", { locale: ptBR }),
        normal: counts.normal,
        alerts: counts.alerts,
      };
    });
  }, [executions]);

  const chartConfig = {
    normal: {
      label: t('dashboard.normalEvents'),
      color: 'hsl(var(--success))',
    },
    alerts: {
      label: t('dashboard.alerts'),
      color: 'hsl(var(--destructive))',
    },
  };

  const totalNormal = chartData.reduce((sum, d) => sum + d.normal, 0);
  const totalAlerts = chartData.reduce((sum, d) => sum + d.alerts, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          {t('dashboard.eventsLast30Days')}
        </CardTitle>
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1 text-success">
            <span className="w-3 h-3 rounded-full bg-success" />
            {totalNormal} {t('dashboard.normalEvents').toLowerCase()}
          </span>
          <span className="flex items-center gap-1 text-destructive">
            <AlertTriangle className="w-3 h-3" />
            {totalAlerts} {t('dashboard.alerts').toLowerCase()}
          </span>
        </div>
      </CardHeader>
      <CardContent className="px-2 pb-3">
        <ChartContainer config={chartConfig} className="h-64 w-full">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickMargin={4}
              tickLine={false}
              axisLine={false}
              interval={6}
            />
            <YAxis
              width={40}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
            />
            <ChartTooltip
              content={<ChartTooltipContent />}
              labelFormatter={(value, payload) => {
                if (payload && payload[0]) {
                  return payload[0].payload.fullDate;
                }
                return value;
              }}
            />
            <Line
              type="monotone"
              dataKey="normal"
              stroke="hsl(var(--success))"
              strokeWidth={2}
              dot={false}
              name="Normais"
            />
            <Line
              type="monotone"
              dataKey="alerts"
              stroke="hsl(var(--destructive))"
              strokeWidth={2}
              dot={false}
              name="Alertas"
            />
            <ChartLegend content={<ChartLegendContent />} />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}